import { useEffect, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";

import { ApiError } from "@/lib/api/client";
import {
  usePrescriberIdentity,
  useSavePrescriberIdentity,
  useUploadPrescriberSignature,
} from "@/lib/api/clinicianHooks";
import { US_STATES } from "@/lib/usStates";
import type {
  ClinicalTeamMember,
  PrescriberCredential,
  PrescriberIdentity,
  PrescriberIdentityFields,
  PrescriberIdentityIssue,
} from "@/types/clinician";

const inputClass =
  "w-full rounded border border-line bg-page px-10 py-8 type-body-sm focus-visible:outline-none focus-visible:shadow-focus";

const CREDENTIALS: PrescriberCredential[] = [
  "MD",
  "DO",
  "NP",
  "PA",
  "FNP",
  "APRN",
  "DNP",
];
const SUPERVISED: PrescriberCredential[] = ["NP", "PA", "FNP", "APRN", "DNP"];

/** Pharmacies that must register a prescriber's NPI before their scripts ship. */
export const PHARMACIES: { key: string; label: string }[] = [
  { key: "precisionmeds", label: "Precision (PPI)" },
  { key: "optiorx", label: "OptioRx" },
  { key: "the_pharmacy_hub", label: "The Pharmacy Hub (BondRx)" },
];

const STATE_CODES = new Set(US_STATES.map((state) => state.value));

function pharmacyLabel(key: string) {
  return PHARMACIES.find((pharmacy) => pharmacy.key === key)?.label ?? key;
}

/** Plain-language text for a script-identity readiness gap. */
export function identityIssueText(issue: PrescriberIdentityIssue): string {
  const items = issue.items ?? [];
  switch (issue.code) {
    case "identity_missing":
      return "Script identity missing — can't approve prescriptions";
    case "licensed_states_missing":
      return "No licensed states — can't take patients or approve";
    case "license_numbers_missing":
      return `License numbers missing: ${items.join(", ")}`;
    case "supervising_physician_missing":
      return items.length
        ? `No supervising physician or full practice authority — can't prescribe in ${items.join(", ")}`
        : "No supervising physician or full practice authority set";
    case "supervising_physician_unavailable":
      return `Supervising physician inactive or unlicensed — can't prescribe in ${items.join(", ")}`;
    case "signature_missing":
      return "No signature image (scripts use a typed signature)";
    case "pharmacy_registration_unconfirmed":
      return `NPI not confirmed at ${items.map(pharmacyLabel).join(", ")}`;
    default:
      return issue.code.replaceAll("_", " ");
  }
}

export function IdentityIssueChips({
  issues,
}: {
  issues: PrescriberIdentityIssue[];
}) {
  if (!issues.length) return null;
  return (
    <div className="flex flex-wrap gap-5">
      {issues.map((issue) => (
        <span
          key={issue.code}
          className={`rounded px-6 py-2 type-label-xs ${issue.code === "identity_missing" ? "bg-danger/10 text-danger" : "bg-warning/15 text-warning"}`}
        >
          {identityIssueText(issue)}
        </span>
      ))}
    </div>
  );
}

/** Two-letter codes from free text ("ca, ny tx"), with any unknown entries. */
export function parseStateCodes(text: string): {
  states: string[];
  invalid: string[];
} {
  const tokens = text
    .split(/[\s,;]+/)
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);
  const states = [...new Set(tokens.filter((code) => STATE_CODES.has(code)))];
  const invalid = tokens.filter((code) => !STATE_CODES.has(code));
  return { states, invalid };
}

export const ALL_STATE_CODES = US_STATES.map((state) => state.value);

function credentialOf(name: string | null | undefined): string | null {
  const match = /,\s*([A-Z]+)$/.exec(name ?? "");
  return match ? match[1] : null;
}

function blankIdentity(member: ClinicalTeamMember): PrescriberIdentityFields {
  const parts = (member.display_name ?? "").trim().split(/\s+/);
  return {
    first_name: parts.length > 1 ? parts[0] : "",
    last_name: parts.length > 1 ? parts[parts.length - 1] : "",
    credential: "MD",
    npi: "",
    dea_number: "",
    phone: "",
    address_street: "",
    address_line2: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    state_licenses: [],
    supervising_physicians: {},
    pharmacy_registrations: {},
    cosign_sample_percent: null,
    full_practice_states: [],
  };
}

function fromIdentity(identity: PrescriberIdentity): PrescriberIdentityFields {
  return {
    first_name: identity.first_name ?? "",
    last_name: identity.last_name ?? "",
    credential: identity.credential ?? "MD",
    npi: identity.npi ?? "",
    dea_number: identity.dea_number ?? "",
    phone: identity.phone ?? "",
    address_street: identity.address_street ?? "",
    address_line2: identity.address_line2 ?? "",
    address_city: identity.address_city ?? "",
    address_state: identity.address_state ?? "",
    address_zip: identity.address_zip ?? "",
    state_licenses: (identity.state_licenses ?? []).map((license) => ({
      state: license.state ?? "",
      number: license.number ?? "",
      expires_on: license.expires_on ?? "",
    })),
    supervising_physicians: { ...(identity.supervising_physicians ?? {}) },
    pharmacy_registrations: { ...(identity.pharmacy_registrations ?? {}) },
    cosign_sample_percent: identity.cosign_sample_percent ?? null,
    full_practice_states: [...(identity.full_practice_states ?? [])],
  };
}

function cleaned(form: PrescriberIdentityFields): PrescriberIdentityFields {
  const optional = (value: string | null | undefined) =>
    value && value.trim() ? value.trim() : null;
  return {
    ...form,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    npi: form.npi.replace(/\D/g, ""),
    dea_number: optional(form.dea_number),
    phone: optional(form.phone),
    address_street: optional(form.address_street),
    address_line2: optional(form.address_line2),
    address_city: optional(form.address_city),
    address_state: optional(form.address_state)?.toUpperCase() ?? null,
    address_zip: optional(form.address_zip),
    state_licenses: form.state_licenses
      .filter((license) => license.state && license.number.trim())
      .map((license) => ({
        state: license.state.toUpperCase(),
        number: license.number.trim(),
        expires_on: license.expires_on || null,
      })),
    supervising_physicians: Object.fromEntries(
      Object.entries(form.supervising_physicians).filter(
        ([state, id]) => state && id,
      ),
    ),
  };
}

/**
 * Supervisor editor for the identity printed on a prescriber's scripts:
 * legal name, credential, NPI, practice address, state licenses, NP/PA
 * supervising physician by state, pharmacy NPI registration and signature.
 * Changes apply to approvals from now on; approved orders keep theirs.
 */
export function PrescriberIdentityEditor({
  member,
  clinicians,
}: {
  member: ClinicalTeamMember;
  clinicians: ClinicalTeamMember[];
}) {
  const identity = usePrescriberIdentity(member.clinician_id);
  const save = useSavePrescriberIdentity(member.clinician_id);
  const upload = useUploadPrescriberSignature(member.clinician_id);
  const [form, setForm] = useState<PrescriberIdentityFields>(() =>
    blankIdentity(member),
  );
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [signature, setSignature] = useState<File | null>(null);
  // Free text for full-practice states, parsed into codes as it's typed.
  const [fullPracticeText, setFullPracticeText] = useState("");

  // Load the saved identity once per saved version, never over unsaved edits.
  useEffect(() => {
    const data = identity.data;
    const version = data?.identity?.updated_at ?? "new";
    if (!data || loadedFor === version) return;
    setForm(
      data.identity ? fromIdentity(data.identity) : blankIdentity(member),
    );
    setFullPracticeText((data.identity?.full_practice_states ?? []).join(", "));
    setLoadedFor(version);
  }, [identity.data, loadedFor, member]);

  if (identity.isLoading) {
    return <p className="type-body-sm text-ink-muted">Loading identity…</p>;
  }
  if (identity.isError || !identity.data) {
    return (
      <p className="type-body-sm text-danger">
        The prescriber identity could not be loaded.
      </p>
    );
  }

  const supervised = SUPERVISED.includes(form.credential);
  const physicians = clinicians.filter(
    (candidate) =>
      candidate.clinician_id !== member.clinician_id &&
      candidate.provider_type === "prescriber" &&
      ["MD", "DO"].includes(credentialOf(candidate.prescriber_name) ?? ""),
  );
  const npiDigits = form.npi.replace(/\D/g, "");
  const missingLicenseStates = member.licensed_states.filter(
    (state) => !form.state_licenses.some((license) => license.state === state),
  );
  const fullPractice = parseStateCodes(fullPracticeText);
  // Licensed states where an approval would be refused: no supervising
  // physician (or "any state" default) and no full practice authority.
  const uncoveredStates = supervised
    ? member.licensed_states.filter(
        (state) =>
          !form.supervising_physicians[state] &&
          !form.supervising_physicians["*"] &&
          !fullPractice.states.includes(state),
      )
    : [];
  const canSave =
    form.first_name.trim() &&
    form.last_name.trim() &&
    npiDigits.length === 10 &&
    !save.isPending;
  const set = (patch: Partial<PrescriberIdentityFields>) => {
    setForm((current) => ({ ...current, ...patch }));
    setNotice(null);
  };

  return (
    <div className="flex flex-col gap-14">
      {identity.data.uses_legacy_identity && !identity.data.identity && (
        <p className="rounded border border-line bg-sunken p-10 type-body-sm text-ink-muted">
          Scripts approved by this prescriber use the configured default
          prescriber. Saving an identity here switches their future approvals to
          it.
        </p>
      )}
      <IdentityIssueChips issues={identity.data.issues} />

      <div className="grid gap-12 lg:grid-cols-4">
        <label className="type-label-sm">
          Legal first name
          <input
            className={`${inputClass} mt-5`}
            value={form.first_name}
            onChange={(event) => set({ first_name: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          Legal last name
          <input
            className={`${inputClass} mt-5`}
            value={form.last_name}
            onChange={(event) => set({ last_name: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          Credential
          <select
            className={`${inputClass} mt-5`}
            value={form.credential}
            onChange={(event) =>
              set({ credential: event.target.value as PrescriberCredential })
            }
          >
            {CREDENTIALS.map((credential) => (
              <option key={credential} value={credential}>
                {credential}
              </option>
            ))}
          </select>
        </label>
        <label className="type-label-sm">
          NPI
          <input
            className={`${inputClass} mt-5`}
            inputMode="numeric"
            value={form.npi}
            onChange={(event) => set({ npi: event.target.value })}
            placeholder="10 digits"
          />
          {form.npi && npiDigits.length !== 10 && (
            <span className="mt-3 block type-label-xs text-danger">
              An NPI has 10 digits.
            </span>
          )}
        </label>
        <label className="type-label-sm">
          DEA (optional)
          <input
            className={`${inputClass} mt-5`}
            value={form.dea_number ?? ""}
            onChange={(event) => set({ dea_number: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          Practice phone
          <input
            className={`${inputClass} mt-5`}
            value={form.phone ?? ""}
            onChange={(event) => set({ phone: event.target.value })}
          />
        </label>
        <label className="type-label-sm lg:col-span-2">
          Practice street address
          <input
            className={`${inputClass} mt-5`}
            value={form.address_street ?? ""}
            onChange={(event) => set({ address_street: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          Suite / unit
          <input
            className={`${inputClass} mt-5`}
            value={form.address_line2 ?? ""}
            onChange={(event) => set({ address_line2: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          City
          <input
            className={`${inputClass} mt-5`}
            value={form.address_city ?? ""}
            onChange={(event) => set({ address_city: event.target.value })}
          />
        </label>
        <label className="type-label-sm">
          State
          <select
            className={`${inputClass} mt-5`}
            value={form.address_state ?? ""}
            onChange={(event) => set({ address_state: event.target.value })}
          >
            <option value="">—</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>
                {state.value}
              </option>
            ))}
          </select>
        </label>
        <label className="type-label-sm">
          ZIP
          <input
            className={`${inputClass} mt-5`}
            value={form.address_zip ?? ""}
            onChange={(event) => set({ address_zip: event.target.value })}
          />
        </label>
      </div>

      <section className="flex flex-col gap-8">
        <div className="flex flex-wrap items-center justify-between gap-8">
          <p className="type-label-sm">State licenses</p>
          <div className="flex flex-wrap gap-6">
            {missingLicenseStates.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  set({
                    state_licenses: [
                      ...form.state_licenses,
                      ...missingLicenseStates.map((state) => ({
                        state,
                        number: "",
                        expires_on: "",
                      })),
                    ],
                  })
                }
                className="rounded border border-line px-8 py-5 type-label-xs hover:bg-page"
              >
                Add rows for {missingLicenseStates.length} licensed states
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                set({
                  state_licenses: [
                    ...form.state_licenses,
                    { state: "", number: "", expires_on: "" },
                  ],
                })
              }
              className="inline-flex items-center gap-4 rounded border border-line px-8 py-5 type-label-xs hover:bg-page"
            >
              <Plus size={12} /> Add license
            </button>
          </div>
        </div>
        {form.state_licenses.length ? (
          <div className="rounded border border-line bg-page divide-y divide-line-subtle">
            {form.state_licenses.map((license, index) => (
              <div
                key={index}
                className="grid grid-cols-[6rem_1fr_11rem_auto] items-center gap-8 px-8 py-6"
              >
                <select
                  aria-label="License state"
                  className="rounded border border-line bg-raised px-6 py-5 type-body-sm"
                  value={license.state}
                  onChange={(event) =>
                    set({
                      state_licenses: form.state_licenses.map(
                        (row, rowIndex) =>
                          rowIndex === index
                            ? { ...row, state: event.target.value }
                            : row,
                      ),
                    })
                  }
                >
                  <option value="">—</option>
                  {US_STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.value}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="License number"
                  className="rounded border border-line bg-raised px-8 py-5 type-body-sm"
                  value={license.number}
                  placeholder="License number"
                  onChange={(event) =>
                    set({
                      state_licenses: form.state_licenses.map(
                        (row, rowIndex) =>
                          rowIndex === index
                            ? { ...row, number: event.target.value }
                            : row,
                      ),
                    })
                  }
                />
                <input
                  aria-label="License expiry"
                  type="date"
                  className="rounded border border-line bg-raised px-8 py-5 type-body-sm"
                  value={license.expires_on ?? ""}
                  onChange={(event) =>
                    set({
                      state_licenses: form.state_licenses.map(
                        (row, rowIndex) =>
                          rowIndex === index
                            ? { ...row, expires_on: event.target.value }
                            : row,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  aria-label="Remove license"
                  onClick={() =>
                    set({
                      state_licenses: form.state_licenses.filter(
                        (_, rowIndex) => rowIndex !== index,
                      ),
                    })
                  }
                  className="rounded border border-line p-5 text-danger hover:bg-danger/5"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="type-body-sm text-ink-muted">
            No license numbers recorded. Routing uses the licensed states above;
            the number for the patient's state is printed on scripts when
            present.
          </p>
        )}
      </section>

      {supervised && (
        <section className="flex flex-col gap-8">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div>
              <p className="type-label-sm">Supervising physician</p>
              <p className="type-label-xs text-ink-muted">
                Printed on {form.credential} scripts for the patient's state.
                “Any state” applies where a state has no entry.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const next =
                  ["*", ...member.licensed_states].find(
                    (state) => !(state in form.supervising_physicians),
                  ) ?? "";
                set({
                  supervising_physicians: {
                    ...form.supervising_physicians,
                    [next]: "",
                  },
                });
              }}
              className="inline-flex items-center gap-4 rounded border border-line px-8 py-5 type-label-xs hover:bg-page"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          {!physicians.length && (
            <p className="type-body-sm text-warning">
              Save an MD or DO prescriber's identity first; only they can
              supervise.
            </p>
          )}
          {Object.entries(form.supervising_physicians).map(
            ([state, supervisorId], index) => (
              <div
                key={`${state}-${index}`}
                className="grid grid-cols-[9rem_1fr_auto] items-center gap-8"
              >
                <select
                  aria-label="Supervision state"
                  className="rounded border border-line bg-raised px-6 py-5 type-body-sm"
                  value={state}
                  onChange={(event) => {
                    const entries = Object.entries(
                      form.supervising_physicians,
                    ).map(([key, value]) =>
                      key === state
                        ? [event.target.value, value]
                        : [key, value],
                    );
                    set({
                      supervising_physicians: Object.fromEntries(entries),
                    });
                  }}
                >
                  <option value="">—</option>
                  <option value="*">Any state</option>
                  {US_STATES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Supervising physician"
                  className="rounded border border-line bg-raised px-6 py-5 type-body-sm"
                  value={supervisorId}
                  onChange={(event) =>
                    set({
                      supervising_physicians: {
                        ...form.supervising_physicians,
                        [state]: event.target.value,
                      },
                    })
                  }
                >
                  <option value="">Choose a physician</option>
                  {physicians.map((physician) => (
                    <option
                      key={physician.clinician_id}
                      value={physician.clinician_id}
                    >
                      {physician.prescriber_name ||
                        physician.display_name ||
                        physician.email}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Remove supervising physician"
                  onClick={() =>
                    set({
                      supervising_physicians: Object.fromEntries(
                        Object.entries(form.supervising_physicians).filter(
                          ([key]) => key !== state,
                        ),
                      ),
                    })
                  }
                  className="rounded border border-line p-5 text-danger hover:bg-danger/5"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ),
          )}
          <div className="flex flex-col gap-5 border-t border-line-subtle pt-8">
            <p className="type-label-sm">Full practice authority</p>
            <p className="type-label-xs text-ink-muted">
              States where this {form.credential} prescribes without a
              supervising physician. Scripts there name none, and those
              approvals aren&rsquo;t sampled for chart review. A state can&rsquo;t
              have both.
            </p>
            <input
              aria-label="Full practice authority states"
              value={fullPracticeText}
              onChange={(event) => {
                setFullPracticeText(event.target.value);
                set({
                  full_practice_states: parseStateCodes(event.target.value)
                    .states,
                });
              }}
              placeholder="e.g. ID, MT, OR"
              className="rounded border border-line bg-raised px-8 py-6 type-body-sm"
            />
            {fullPractice.invalid.length > 0 && (
              <p className="type-label-xs text-danger">
                Not a state: {fullPractice.invalid.join(", ")}
              </p>
            )}
            {uncoveredStates.length > 0 && (
              <p className="type-label-xs text-warning">
                Can&rsquo;t prescribe in {uncoveredStates.join(", ")} until each
                has a supervising physician or full practice authority.
              </p>
            )}
          </div>
          <label className="flex flex-wrap items-center gap-8 type-label-xs text-ink-muted">
            CHART REVIEW SAMPLE
            <input
              type="number"
              min={0}
              max={100}
              value={form.cosign_sample_percent ?? ""}
              placeholder="20"
              onChange={(event) =>
                setForm({
                  ...form,
                  cosign_sample_percent:
                    event.target.value === ""
                      ? null
                      : Math.max(0, Math.min(100, Number(event.target.value))),
                })
              }
              className="w-[6rem] rounded border border-line bg-page px-8 py-6 type-body-sm text-ink"
            />
            <span className="normal-case">
              % of each month's approvals go to the supervising physician to
              cosign (blank = 20%).
            </span>
          </label>
        </section>
      )}

      <section className="flex flex-col gap-6">
        <p className="type-label-sm">Pharmacy NPI registration</p>
        <p className="type-label-xs text-ink-muted">
          Each pharmacy matches the NPI against its own prescriber list. Confirm
          once they have registered it.
        </p>
        <div className="flex flex-wrap gap-14">
          {PHARMACIES.map((pharmacy) => {
            const confirmed =
              form.pharmacy_registrations[pharmacy.key]?.confirmed_at ?? "";
            return (
              <label
                key={pharmacy.key}
                className="inline-flex items-center gap-6 type-body-sm"
              >
                <input
                  type="checkbox"
                  checked={Boolean(confirmed)}
                  onChange={(event) => {
                    const next = { ...form.pharmacy_registrations };
                    if (event.target.checked) {
                      next[pharmacy.key] = {
                        confirmed_at: new Date().toISOString().slice(0, 10),
                      };
                    } else {
                      delete next[pharmacy.key];
                    }
                    set({ pharmacy_registrations: next });
                  }}
                />
                {pharmacy.label}
                {confirmed && (
                  <span className="type-label-xs text-ink-muted">
                    {confirmed}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-10">
        <button
          type="button"
          disabled={!canSave}
          onClick={async () => {
            setError(null);
            setNotice(null);
            try {
              await save.mutateAsync(cleaned(form));
              setNotice(
                "Identity saved. It applies to this prescriber's approvals from now on.",
              );
            } catch (cause) {
              setError(
                cause instanceof ApiError
                  ? cause.message
                  : "The identity could not be saved.",
              );
            }
          }}
          className="rounded bg-ink px-12 py-7 type-label-sm text-ink-onAccent disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save identity"}
        </button>
        {notice && <p className="type-body-sm text-success">{notice}</p>}
        {error && <p className="type-body-sm text-danger">{error}</p>}
      </div>

      <section className="flex flex-col gap-8 border-t border-line-subtle pt-12">
        <p className="type-label-sm">Signature</p>
        {identity.data.signature_url ? (
          <img
            src={identity.data.signature_url}
            alt={`Signature on file for ${form.first_name} ${form.last_name}`}
            className="max-h-[6rem] max-w-[24rem] rounded border border-line bg-raised p-6 object-contain"
          />
        ) : (
          <p className="type-body-sm text-ink-muted">
            No signature image. Scripts print a typed /s/ signature.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-8">
          <input
            type="file"
            accept="image/png,image/jpeg"
            aria-label="Signature image"
            onChange={(event) => setSignature(event.target.files?.[0] ?? null)}
            className="type-body-sm"
          />
          <button
            type="button"
            disabled={!signature || !identity.data.identity || upload.isPending}
            onClick={async () => {
              if (!signature) return;
              setError(null);
              try {
                await upload.mutateAsync(signature);
                setSignature(null);
                setNotice("Signature uploaded.");
              } catch (cause) {
                setError(
                  cause instanceof ApiError
                    ? cause.message
                    : "The signature could not be uploaded.",
                );
              }
            }}
            className="inline-flex items-center gap-5 rounded border border-line px-10 py-6 type-label-sm hover:bg-page disabled:opacity-40"
          >
            <Upload size={13} />
            {upload.isPending ? "Uploading…" : "Upload signature"}
          </button>
          {!identity.data.identity && (
            <span className="type-label-xs text-ink-muted">
              Save the identity first.
            </span>
          )}
        </div>
        <p className="type-label-xs text-ink-muted">
          PNG or JPEG under 2 MB, ideally dark ink on a white background.
        </p>
      </section>
    </div>
  );
}
